import type { ReactNode } from 'react';
import type { PublicWisetackDisclosure } from '@/types/public';
import { WISETACK_DISCLOSURE_ANCHORS, wisetackDisclosureFontSize } from '@/lib/wisetack/disclosure';

function linkify(text: string): ReactNode[] {
  const matches: Array<{ start: number; end: number; href: string }> = [];
  for (const anchor of WISETACK_DISCLOSURE_ANCHORS) {
    let from = 0;
    let idx = text.indexOf(anchor.phrase, from);
    while (idx !== -1) {
      matches.push({ start: idx, end: idx + anchor.phrase.length, href: anchor.href });
      if (anchor.once) break;
      from = idx + anchor.phrase.length;
      idx = text.indexOf(anchor.phrase, from);
    }
  }
  matches.sort((a, b) => a.start - b.start);

  const parts: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((match, index) => {
    if (match.start < cursor) return; // skip any overlap
    if (match.start > cursor) parts.push(text.slice(cursor, match.start));
    parts.push(
      <a
        key={`${match.href}-${index}`}
        className="underline"
        href={match.href}
        target="_blank"
        rel="noreferrer"
      >
        {text.slice(match.start, match.end)}
      </a>,
    );
    cursor = match.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

/**
 * The disclosure as React nodes, with the compliance phrases hyperlinked in
 * place. The email variant of the same rules lives in
 * `buildWisetackDisclosureHtml`; both read their anchors from one module.
 */
export function WisetackDisclosure({
  disclosure,
  className,
}: {
  disclosure: PublicWisetackDisclosure;
  className?: string;
}) {
  return (
    <p
      className={className}
      data-testid="wisetack-disclosure"
      style={{ fontSize: wisetackDisclosureFontSize(disclosure.minimumFontSizePx) }}
    >
      {linkify(disclosure.text)}
    </p>
  );
}
